import { TrendingUp, TrendingDown, ChevronRight } from "lucide-react";
import { formatPrice, formatPct } from "../lib/format";
import { Button } from "./ui/button";

const CATEGORY_LABEL = { crypto: "Crypto", stock: "Action", commodity: "Matière", rate: "Taux" };

function AssetIcon({ card }) {
  if (card.image) {
    return (
      <img
        src={card.image}
        alt={card.key}
        className="w-9 h-9 rounded-full ring-1 ring-white/10"
        onError={(e) => (e.currentTarget.style.visibility = "hidden")}
      />
    );
  }
  const bg =
    card.category === "stock"
      ? "bg-sky-500/15 text-sky-300"
      : card.category === "commodity"
      ? "bg-amber-500/15 text-amber-300"
      : "bg-violet-500/15 text-violet-300";
  return (
    <div className={`w-9 h-9 rounded-full grid place-items-center text-[11px] font-bold ${bg}`}>
      {card.key.slice(0, 3)}
    </div>
  );
}

/** Inline SVG sparkline — zero-dep, fast. */
function Sparkline({ data, up }) {
  if (!data || data.length < 2) return <div className="h-8 text-[10px] text-zinc-600 grid place-items-center">Chargement…</div>;
  const w = 120, h = 32, pad = 2;
  const min = Math.min(...data), max = Math.max(...data);
  const range = max - min || 1;
  const step = (w - pad * 2) / (data.length - 1);
  const pts = data
    .map((v, i) => `${(pad + i * step).toFixed(2)},${(h - pad - ((v - min) / range) * (h - pad * 2)).toFixed(2)}`)
    .join(" ");
  const stroke = up ? "#00BF63" : "#f43f5e";
  const gradId = `g-${up ? "u" : "d"}`;
  const areaPts = `${pad},${h} ${pts} ${w - pad},${h}`;
  return (
    <svg width={w} height={h} className="overflow-visible" data-testid="asset-sparkline">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.35" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={areaPts} fill={`url(#${gradId})`} />
      <polyline points={pts} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

export default function AssetCard({ card, timeframe = "24h", onOpen }) {
  const changeField = timeframe === "7d" ? "change_7d_pct" : "change_24h_pct";
  const change = card?.[changeField];
  const available = card.price != null && change != null;
  const up = (change ?? 0) >= 0;
  const spark = timeframe === "7d" ? card.sparkline_7d : card.sparkline_24h;
  const priceStr = !available
    ? "Indisponible"
    : card.category === "rate"
    ? `${Number(card.price ?? 0).toFixed(2)}%`
    : formatPrice(card.price, card.currency); // devise de cotation (ex. yens pour une action de Tokyo)

  return (
    <div
      className="group relative p-5 rounded-xl border border-white/5 bg-gradient-to-br from-white/[0.03] to-transparent hover:border-emerald-500/30 hover:from-emerald-500/[0.04] transition-all w-[280px] shrink-0"
      data-testid={`asset-card-${card.key}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <AssetIcon card={card} />
          <div>
            <div className="font-medium text-sm leading-tight">{card.name}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-0.5">
              {card.key} · {CATEGORY_LABEL[card.category]}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-baseline justify-between">
        <div
          className={`font-display font-black text-2xl font-mono-num ${!available ? "text-zinc-500 text-lg" : ""}`}
          data-testid={`asset-price-${card.key}`}
        >
          {priceStr}
        </div>
        {available && (
          <span
            className={`inline-flex items-center gap-1 text-xs font-mono-num px-2 py-1 rounded ${
              up ? "text-emerald-400 bg-emerald-500/10" : "text-rose-400 bg-rose-500/10"
            }`}
            data-testid={`asset-change-${card.key}`}
          >
            {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {formatPct(change)}
          </span>
        )}
      </div>

      <div className="mt-3">
        {available ? <Sparkline data={spark} up={up} /> : <div className="h-8" />}
      </div>

      <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-[11px] text-zinc-500 uppercase tracking-[0.15em]">
        <span>{timeframe === "7d" ? "Sur 7 jours" : "Sur 24 heures"}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onOpen?.(card)}
          className="h-7 px-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
          data-testid={`asset-open-${card.key}`}
        >
          Voir plus <ChevronRight className="w-3 h-3 ml-0.5" />
        </Button>
      </div>
    </div>
  );
}
